import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import {
  Coins, ShieldCheck, Zap, Users, TrendingUp, Wallet, ArrowRight, CheckCircle2,
} from "lucide-react";

const fmtAmt = (n) => Number(n || 0).toLocaleString();
const rangeText = (p) =>
  Number(p.max_amount) >= 100000
    ? `${fmtAmt(p.min_amount)}+ USDT`
    : `${fmtAmt(p.min_amount)} – ${fmtAmt(p.max_amount)} USDT`;

const features = [
  { icon: ShieldCheck, title: "Real Blockchain Verify", desc: "TRC20 & BEP20 deposits verified directly on-chain — no fake balance." },
  { icon: Zap, title: "Automatic Credit", desc: "Your balance is credited automatically the moment a deposit confirms." },
  { icon: TrendingUp, title: "Daily ROI", desc: "Daily income is auto-calculated according to your business rules." },
  { icon: Users, title: "Multi-level Referral", desc: "3-level referral income — automatic commission from your team." },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    api.get("/plans").then(({ data }) => setPlans(data.plans || [])).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/70 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Coins className="h-7 w-7 text-emerald-400" />
            <span className="text-xl font-extrabold">ERAN <span className="text-emerald-400">USD</span></span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/business-plan" data-testid="header-plan-link"
              className="hidden sm:block text-sm text-slate-300 hover:text-white px-3">Business Plan</Link>
            {user ? (
              <Button data-testid="go-dashboard-btn" onClick={() => navigate("/dashboard")}
                className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full">
                Dashboard
              </Button>
            ) : (
              <>
                <Link to="/login" data-testid="header-login-link"
                  className="text-sm text-slate-300 hover:text-white px-3">Login</Link>
                <Button data-testid="header-register-btn" onClick={() => navigate("/register")}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full">Get Started</Button>
              </>
            )}
          </div>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <Badge className="mb-5 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-full px-3 py-1">
            <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse mr-2" /> Live On-chain Automation
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-none">
            Real USDT.<br /><span className="text-emerald-400">Automatic</span> Income.
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-300 max-w-lg leading-relaxed">
            Deposit your USDT (TRC20 / BEP20) — the instant it's verified on-chain your balance
            is auto-credited, with daily ROI and referral income all automatic. Withdrawals are
            admin-approved & secure.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Button data-testid="hero-start-btn" size="lg" onClick={() => navigate("/register")}
              className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full text-base px-8 h-12">
              Start Earning <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button data-testid="hero-login-btn" size="lg" variant="outline" onClick={() => navigate("/login")}
              className="rounded-full text-base px-8 h-12 border-slate-700 hover:bg-white/5">
              I have an account
            </Button>
          </div>
          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-400">
            {["On-chain verified", "No fake balance", "TRC20 + BEP20"].map((t) => (
              <span key={t} className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-emerald-400" />{t}</span>
            ))}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.15 }}
          className="card-surface rounded-3xl p-6 glow-emerald">
          <div className="flex items-center justify-between mb-6">
            <span className="text-sm text-slate-400 font-mono">PORTFOLIO</span>
            <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20">USDT</Badge>
          </div>
          <div className="text-4xl font-extrabold font-mono">12,480.<span className="text-slate-500">50</span></div>
          <div className="text-emerald-400 text-sm mt-1">+ 312.00 today (daily ROI)</div>
          <div className="grid grid-cols-3 gap-3 mt-6">
            {[["TRC20", "Tron"], ["BEP20", "BSC"], ["Referral", "3 levels"]].map(([a, b]) => (
              <div key={a} className="rounded-xl bg-white/5 p-3">
                <div className="text-xs text-slate-400">{a}</div>
                <div className="text-sm font-semibold mt-1">{b}</div>
              </div>
            ))}
          </div>
          <div className="mt-6 space-y-2">
            {plans.map((p) => (
              <div key={p.id} className="flex items-center justify-between rounded-xl border border-border p-3">
                <span className="text-sm">{p.name}</span>
                <span className="text-emerald-400 font-semibold font-mono text-sm">{p.daily_roi_pct}%/day</span>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-center mb-12">
          Fully Automatic Platform
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className="card-surface rounded-2xl p-6 hover:border-emerald-500/40 transition-colors">
              <f.icon className="h-8 w-8 text-emerald-400 mb-4" />
              <h3 className="text-lg font-semibold mb-2">{f.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-3 gap-6">
          {plans.map((p) => (
            <div key={p.id} className="card-surface rounded-2xl p-8 text-center hover:border-emerald-500/40 transition-colors">
              <div className="text-sm text-slate-400 uppercase tracking-wider">{p.name}</div>
              <div className="text-5xl font-extrabold font-mono text-emerald-400 my-4">{p.daily_roi_pct}%</div>
              <div className="text-xs text-slate-500 mb-4">per day · {p.duration_days} days</div>
              <div className="text-sm text-slate-300 mb-6">{rangeText(p)}</div>
              <Button onClick={() => navigate("/register")} className="w-full rounded-full bg-emerald-500 hover:bg-emerald-600 text-white">
                Choose Plan
              </Button>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-border py-8 text-center text-sm text-slate-500">
        ERAN USD · Real on-chain USDT platform · © 2026
      </footer>
    </div>
  );
}
